import { isMapId, type MapId } from "@69ly/maps";
import { frameAtTick, PF, type Grenade, type Movement, type Replay, type Stuff } from "@69ly/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { CopyButton } from "../components/CopyButton.tsx";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { StatusControl } from "../components/StatusControl.tsx";
import { tallyStuffs, type GrenadeMatch } from "../demo/matching.ts";
import type { RoundIndex } from "../demo/scene.ts";
import type { StuffDraft } from "./StuffForm.tsx";
import { api } from "../lib/api.ts";
import { useCallouts } from "../lib/callouts.ts";
import { matchKeys } from "../lib/demo-queries.ts";
import { errorText } from "../lib/errors.ts";

const SIDE_CLASS = { t: "side-t", ct: "side-ct" } as const;
const clock = (s: number) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

/** Seconds since the round's start, for a tick. */
const inRound = (r: Replay, round: RoundIndex, tick: number) => (tick - round.round.startTick) / r.tickRate;

function Who({ replay, index, numbers, side }: { replay: Replay; index: number; numbers: number[]; side: "t" | "ct" }) {
  return (
    <span className={`demo-who ${SIDE_CLASS[side]}`}>
      <span className="demo-num-mini">{numbers[index]}</span>
      {replay.players[index]?.name ?? "?"}
    </span>
  );
}

/**
 * How the throw was made, from the thrower's movement around the release: running,
 * walking or standing still, and whether their feet were off the ground.
 */
function techniqueAt(r: Replay, m: GrenadeMatch): { movement: Movement; jump: boolean } {
  const g = m.grenade;
  const round = r.rounds.find((x) => g.throwTick >= x.startTick && g.throwTick <= x.postTick);
  if (!round || g.thrower < 0) return { movement: "standing", jump: false };
  const P = r.players.length;
  const at = (tick: number) => {
    const f = frameAtTick(r.columns.frameTicks, round.frameFrom, round.frameTo, tick);
    const i = f * P + g.thrower;
    return { x: r.columns.x[i], y: r.columns.y[i], z: r.columns.z[i], t: r.columns.frameTicks[f], ok: !!(r.columns.flags[i] & PF.present) };
  };
  const a = at(g.throwTick - 24);
  const b = at(g.throwTick - 4);
  const seconds = Math.max(1, b.t - a.t) / r.tickRate;
  const speed = a.ok && b.ok ? Math.hypot(b.x - a.x, b.y - a.y) / seconds : 0;
  // A jump throw is let go at take-off (the jump-throw bind): the feet rise just after it, then come
  // back down. Stairs only go one way, so they rise without coming back.
  const release = m.release;
  const up = at(g.throwTick + 12);
  const down = at(g.throwTick + 48);
  const jump = !!release && up.ok && down.ok && up.z - release.z > 12 && up.z - down.z > 12;
  return { movement: speed > 170 ? "running" : speed > 60 ? "walking" : "standing", jump };
}

/** A new stuff from a throw seen in a demo: spot, view, landing, side and technique filled. */
export function draftFrom(r: Replay, m: GrenadeMatch, note: string): StuffDraft | null {
  if (!m.kind || !m.release || !m.landing) return null;
  const { movement, jump } = techniqueAt(r, m);
  return {
    throw: { x: m.release.x, y: m.release.y, z: m.release.z, pitch: m.release.pitch, yaw: m.release.yaw },
    landing: { x: m.landing[0], y: m.landing[1], z: m.landing[2] },
    grenade: m.kind as Grenade,
    side: m.side,
    movement,
    jump,
    notes: note,
  };
}

function useDismiss(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ grenade, stuffId, undo }: { grenade: number; stuffId: string; undo?: boolean }) =>
      undo ? api<void>("DELETE", `/demos/${demoId}/dismissals?grenade=${grenade}&stuffId=${stuffId}`) : api<void>("POST", `/demos/${demoId}/dismissals`, { grenade, stuffId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.demo(demoId) }),
  });
}

/**
 * The Stuffs tab: this round's grenades that are stuffs of the library (and how well
 * they landed), the others with a button to make one, and each stuff's count over the map.
 */
export function StuffsPanel({
  demoId,
  replay,
  map,
  round,
  matches,
  stuffs,
  dismissed,
  libraryName,
  numbers,
  note,
  onSeek,
}: {
  demoId: string;
  replay: Replay;
  map: MapId;
  round: RoundIndex;
  matches: GrenadeMatch[];
  /** The library's stuffs on this map, null while loading. */
  stuffs: Stuff[] | null;
  dismissed: [number, string][];
  libraryName: string;
  numbers: number[];
  /** "From <match>, round 7": put in a new stuff's notes. */
  note: (roundNumber: number, thrower: string) => string;
  onSeek: (seconds: number) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const callouts = useCallouts();
  const dismiss = useDismiss(demoId);
  const [error, setError] = useState<string | null>(null);
  const r = round.round;
  const here = matches.filter((m) => m.grenade.throwTick >= r.startTick && m.grenade.throwTick <= r.postTick && m.grenade.thrower >= 0);
  const matched = here.filter((m) => m.stuff);
  const unmatched = here.filter((m) => !m.stuff && m.kind);
  const tally = tallyStuffs(matches);
  const byId = new Map((stuffs ?? []).map((s) => [s.id, s]));
  const place = (p: [number, number, number] | null) => (p ? callouts.at(map, p[0], p[1], p[2]) : null);
  const fail = (e: unknown) => setError(errorText(t, e));

  if (!stuffs) return <p className="muted small">{t("common.loading")}</p>;
  return (
    <div className="demo-stuffs">
      <p className="muted small">{t("demos.stuffs.against", { library: libraryName, count: stuffs.length })}</p>
      {error && <p className="field-error small">{error}</p>}

      <section aria-labelledby="ds-round">
        <h3 id="ds-round" className="eyebrow">
          {t("demos.stuffs.thisRound", { n: r.number })}
        </h3>
        {matched.length === 0 ? (
          <p className="muted small">{t("demos.stuffs.noneThisRound")}</p>
        ) : (
          <ul className="demo-stuff-list">
            {matched.map((m) => (
              <li key={m.index} className={`demo-stuff-item${m.onTarget ? " is-on-target" : ""}`}>
                <button type="button" className="demo-stuff-main" onClick={() => onSeek(Math.max(0, inRound(replay, round, m.grenade.throwTick) - 1))}>
                  <GrenadeGlyph grenade={m.kind!} size={18} />
                  <span className="demo-stuff-text">
                    <b>{m.stuff!.title}</b>
                    <span className="small">
                      <Who replay={replay} index={m.grenade.thrower} numbers={numbers} side={m.side} /> · <span className="mono">{clock(inRound(replay, round, m.grenade.throwTick))}</span>
                    </span>
                  </span>
                  <span className={`demo-landed${m.onTarget ? " is-on" : ""}`}>{m.onTarget ? t("demos.stuffs.onTarget") : t("demos.stuffs.off", { n: Math.round(m.landingOff) })}</span>
                </button>
                <div className="demo-stuff-actions">
                  <Link to={`/stuffs/${m.stuff!.id}`} className="btn btn-quiet btn-small">
                    {t("demos.stuffs.open")}
                  </Link>
                  <button
                    type="button"
                    className="btn btn-quiet btn-small"
                    title={t("demos.stuffs.notThisHelp")}
                    disabled={dismiss.isPending}
                    onClick={() => dismiss.mutate({ grenade: m.index, stuffId: m.stuff!.id }, { onError: fail })}
                  >
                    {t("demos.stuffs.notThis")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {unmatched.length > 0 && (
        <section aria-labelledby="ds-new">
          <h3 id="ds-new" className="eyebrow">
            {t("demos.stuffs.notInLibrary")}
          </h3>
          <ul className="demo-stuff-list">
            {unmatched.map((m) => {
              const wrong = dismissed
                .filter(([g]) => g === m.index)
                .map(([, id]) => byId.get(id))
                .filter((s): s is Stuff => !!s);
              const landsAt = place(m.landing);
              const draft = draftFrom(replay, m, note(r.number, replay.players[m.grenade.thrower]?.name ?? ""));
              return (
                <li key={m.index} className="demo-stuff-item">
                  <button type="button" className="demo-stuff-main" onClick={() => onSeek(Math.max(0, inRound(replay, round, m.grenade.throwTick) - 1))}>
                    <GrenadeGlyph grenade={m.kind!} size={18} />
                    <span className="demo-stuff-text">
                      <b>{landsAt ? t("demos.stuffs.toPlace", { grenade: t(`grenade.${m.kind}`), place: landsAt }) : t(`grenade.${m.kind}`)}</b>
                      <span className="small">
                        <Who replay={replay} index={m.grenade.thrower} numbers={numbers} side={m.side} /> · <span className="mono">{clock(inRound(replay, round, m.grenade.throwTick))}</span>
                      </span>
                    </span>
                  </button>
                  <div className="demo-stuff-actions">
                    {draft && isMapId(map) && (
                      <button type="button" className="btn btn-secondary btn-small" onClick={() => void navigate(`/library/${map}/new`, { state: { draft } })}>
                        {t("demos.stuffs.make")}
                      </button>
                    )}
                    {wrong.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="btn btn-quiet btn-small"
                        disabled={dismiss.isPending}
                        onClick={() => dismiss.mutate({ grenade: m.index, stuffId: s.id, undo: true }, { onError: fail })}
                      >
                        {t("demos.stuffs.undoNotThis", { title: s.title })}
                      </button>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section aria-labelledby="ds-map">
        <h3 id="ds-map" className="eyebrow">
          {t("demos.stuffs.overMap")}
        </h3>
        {tally.length === 0 ? (
          <p className="muted small">{stuffs.length === 0 ? t("demos.stuffs.emptyLibrary") : t("demos.stuffs.noneOnMap")}</p>
        ) : (
          <ul className="demo-stuff-list">
            {tally.map((s) => (
              <li key={s.stuff.id} className="demo-stuff-item">
                <div className="demo-stuff-main is-static">
                  <GrenadeGlyph grenade={s.stuff.grenade} size={18} />
                  <span className="demo-stuff-text">
                    <b>{s.stuff.title}</b>
                    <span className="small">{t("demos.stuffs.tally", { thrown: s.thrown, on: s.onTarget, count: s.thrown })}</span>
                    <span className="small muted">{[...s.byPlayer.entries()].map(([i, p]) => `${replay.players[i]?.name ?? "?"} ${p.onTarget}/${p.thrown}`).join(" · ")}</span>
                  </span>
                </div>
                <div className="demo-stuff-status">
                  <StatusControl stuffId={s.stuff.id} status={s.stuff.myStatus} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- utility

interface UtilityLine {
  m: GrenadeMatch;
  /** Enemies blinded: name and seconds. */
  blinded: { victim: number; seconds: number }[];
  teamFlashed: number;
  damage: number;
  hit: number[];
  seconds: number;
}

/** What each grenade did: who a flash blinded and for how long, HE and fire damage, a smoke's time. */
export function utilityOf(r: Replay, matches: GrenadeMatch[], from: number, to: number): UtilityLine[] {
  const weaponIs = (i: number, names: string[]) => names.includes(r.weapons[i] ?? "");
  return matches
    .filter((m) => m.grenade.thrower >= 0 && m.grenade.throwTick >= from && m.grenade.throwTick <= to)
    .map((m) => {
      const g = m.grenade;
      const team = r.players[g.thrower]?.team;
      const line: UtilityLine = { m, blinded: [], teamFlashed: 0, damage: 0, hit: [], seconds: 0 };
      if (g.kind === "flash" && g.detonateTick !== null) {
        for (const [tick, by, victim, cs] of r.blinds) {
          if (by !== g.thrower || Math.abs(tick - g.detonateTick) > 2 || cs < 30) continue;
          if (victim === g.thrower || r.players[victim]?.team === team) line.teamFlashed++;
          else line.blinded.push({ victim, seconds: cs / 100 });
        }
      }
      if ((g.kind === "he" || g.kind === "molotov") && g.detonateTick !== null) {
        const end = g.kind === "he" ? g.detonateTick + 4 : (g.effectEndTick ?? g.detonateTick + 7 * r.tickRate);
        const names = g.kind === "he" ? ["hegrenade"] : ["inferno", "molotov", "incgrenade"];
        for (const [tick, by, to, hp, weapon] of r.hurts) {
          if (by !== g.thrower || tick < g.detonateTick - 2 || tick > end || !weaponIs(weapon, names)) continue;
          if (r.players[to]?.team === team) continue;
          line.damage += hp;
          if (!line.hit.includes(to)) line.hit.push(to);
        }
      }
      if ((g.kind === "smoke" || g.kind === "molotov") && g.detonateTick !== null && g.effectEndTick !== null) line.seconds = (g.effectEndTick - g.detonateTick) / r.tickRate;
      return line;
    });
}

/** The Utility tab: every grenade of the round (or the map), by player, with what it did. */
export function UtilityPanel({ replay, round, matches, numbers, onSeek }: { replay: Replay; round: RoundIndex; matches: GrenadeMatch[]; numbers: number[]; onSeek: (seconds: number) => void }) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<"round" | "map">("round");
  const r = round.round;
  const lines = scope === "round" ? utilityOf(replay, matches, r.startTick, r.postTick) : utilityOf(replay, matches, 0, Number.MAX_SAFE_INTEGER);
  const players = [...new Set(lines.map((l) => l.m.grenade.thrower))];
  const sideIn = (l: UtilityLine) => l.m.side;
  // Replays read before 0.12.0 do not say who a flash blinded: not "no one".
  const knowsBlinds = replay.blinds.length > 0 || !replay.grenades.some((g) => g.kind === "flash");
  return (
    <div className="demo-utility">
      <div className="segmented segmented-small" role="group" aria-label={t("demos.utility.scope")}>
        {(["round", "map"] as const).map((s) => (
          <button key={s} type="button" className="seg" aria-pressed={scope === s} onClick={() => setScope(s)}>
            {s === "round" ? t("demos.utility.round", { n: r.number }) : t("demos.utility.map")}
          </button>
        ))}
      </div>
      {lines.length === 0 && <p className="muted small">{t("demos.utility.none")}</p>}
      {!knowsBlinds && <p className="muted small demo-utility-old">{t("demos.utility.noBlindData")}</p>}
      {players.map((p) => {
        const mine = lines.filter((l) => l.m.grenade.thrower === p);
        const blinded = mine.flatMap((l) => l.blinded);
        const blindSeconds = blinded.reduce((a, b) => a + b.seconds, 0);
        const teamFlashes = mine.reduce((a, l) => a + l.teamFlashed, 0);
        const damage = mine.reduce((a, l) => a + l.damage, 0);
        return (
          <section key={p} className="demo-utility-player" aria-label={replay.players[p]?.name}>
            <h3 className="demo-utility-head">
              <Who replay={replay} index={p} numbers={numbers} side={sideIn(mine[0])} />
              <span className="muted small">
                {[
                  t("demos.utility.thrown", { count: mine.length }),
                  blinded.length ? t("demos.utility.blindedSum", { count: blinded.length, s: blindSeconds.toFixed(1) }) : null,
                  teamFlashes ? t("demos.utility.teamFlashes", { count: teamFlashes }) : null,
                  damage ? t("demos.utility.damage", { n: damage }) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </h3>
            {scope === "round" && (
              <ul className="demo-utility-list">
                {mine.map((l) => (
                  <li key={l.m.index}>
                    <button type="button" onClick={() => onSeek(Math.max(0, inRound(replay, round, l.m.grenade.throwTick) - 1))}>
                      <span className="mono small muted">{clock(inRound(replay, round, l.m.grenade.throwTick))}</span>
                      {l.m.kind ? <GrenadeGlyph grenade={l.m.kind} size={16} /> : <span className="small">{t("demos.utility.decoy")}</span>}
                      <span className="small">
                        {l.m.stuff && <b>{l.m.stuff.title} · </b>}
                        {l.m.grenade.kind === "flash" &&
                          knowsBlinds &&
                          (l.blinded.length
                            ? t("demos.utility.blinded", { names: l.blinded.map((b) => `${replay.players[b.victim]?.name} ${b.seconds.toFixed(1)} s`).join(", ") })
                            : t("demos.utility.noOneBlinded"))}
                        {l.m.grenade.kind === "flash" && l.teamFlashed > 0 && ` · ${t("demos.utility.teamFlashes", { count: l.teamFlashed })}`}
                        {(l.m.grenade.kind === "he" || l.m.grenade.kind === "molotov") &&
                          (l.damage ? t("demos.utility.hit", { n: l.damage, names: l.hit.map((i) => replay.players[i]?.name).join(", ") }) : t("demos.utility.noDamage"))}
                        {l.m.grenade.kind === "smoke" && t("demos.utility.smoke", { s: Math.round(l.seconds) })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Open in CS2

/**
 * Watch this moment in the game itself: the demo file goes in CS2's replays folder,
 * then two console lines open it and jump to the tick, from this player's eyes.
 */
export function OpenInCs2({ fileName, tick, player, inline = false }: { fileName: string; tick: number; player: string | null; inline?: boolean }) {
  const { t } = useTranslation();
  const base = fileName.replace(/\.dem$/i, "");
  const open = `playdemo replays/${base}`;
  const jump = `demo_gototick ${Math.round(tick)}${player ? `; spec_player "${player.replace(/"/g, "")}"` : ""}`;
  const steam = `steam://rungame/730/76561202255233023/+playdemo%20replays/${encodeURIComponent(base)}`;
  return (
    <Wrap inline={inline} title={t("demos.cs2.title")}>
      <ol className="demo-cs2-steps">
        <li>
          {t("demos.cs2.file")} <code>{fileName}</code> → <code>{"…\\Counter-Strike Global Offensive\\game\\csgo\\replays\\"}</code>
        </li>
        <li>
          <span>{t("demos.cs2.open")}</span>
          <code className="demo-cs2-line">{open}</code>
          <CopyButton text={open} />
          <a className="btn btn-quiet btn-small" href={steam}>
            {t("demos.cs2.steam")}
          </a>
        </li>
        <li>
          <span>{t("demos.cs2.jump")}</span>
          <code className="demo-cs2-line">{jump}</code>
          <CopyButton text={jump} />
        </li>
      </ol>
      <p className="muted small">{t("demos.cs2.note")}</p>
    </Wrap>
  );
}

/** Open in CS2 folds in a card on a page; in a popover it is shown as it is, under a title. */
function Wrap({ inline, title, children }: { inline: boolean; title: string; children: React.ReactNode }) {
  if (inline)
    return (
      <div className="demo-cs2 is-inline">
        <h2 className="eyebrow">{title}</h2>
        {children}
      </div>
    );
  return (
    <details className="card demo-cs2">
      <summary>{title}</summary>
      {children}
    </details>
  );
}
